import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { firstValueFrom } from 'rxjs';
import {
  DailyEnergyUpload,
  EnergyCoverageState,
  EnergyTotalMethod,
  HealthHistoryProgressResponse,
  HealthHistoryRunRequest,
  NutritionAmbitionApiService,
  UploadDailyEnergyBatchRequest
} from './nutrition-ambition-api.service';
import { HealthPlatformService } from './health-platform.service';

/** Uploads only a normalized daily aggregate. History is deliberately advanced
 * in bounded native batches; this foreground catch-up always refreshes today. */
@Injectable({ providedIn: 'root' })
export class HealthInboundSyncService {
  private syncing = false;
  private syncingHistory = false;
  constructor(private readonly api: NutritionAmbitionApiService, private readonly health: HealthPlatformService) {}

  async syncTodayIfConfigured(platform: 'AppleHealth' | 'HealthConnect'): Promise<void> {
    if (this.syncing) return;
    const setupKey = platform === 'AppleHealth' ? 'health-apple-outbound-setup-complete' : 'health-connect-inbound-setup-complete';
    const installationKey = platform === 'AppleHealth' ? 'health-apple-installation-id' : 'health-connect-installation-id';
    const [setup, installation] = await Promise.all([Preferences.get({ key: setupKey }), Preferences.get({ key: installationKey })]);
    if (setup.value !== 'true' || !installation.value) return;
    this.syncing = true;
    try {
      const localDate = new Intl.DateTimeFormat('en-CA', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }).format(new Date());
      const energy = await this.health.getDailyEnergySummary(localDate);
      if (!energy || energy.status !== 'data' || energy.activeCalories == null || !energy.observationStart || !energy.observationEnd) return;
      await firstValueFrom(this.api.uploadDailyEnergyBatch(new UploadDailyEnergyBatchRequest({
        installationId: installation.value, platform,
        days: [new DailyEnergyUpload({ localDate, activeCaloriesKcal: energy.activeCalories, totalCaloriesKcal: energy.totalCalories,
          totalMethod: energy.totalCaloriesMethod === 'provider_aggregate' ? EnergyTotalMethod.HealthConnectAggregate : EnergyTotalMethod.AppleActivePlusBasal,
          observationStartUtc: new Date(energy.observationStart), observationEndUtc: new Date(energy.observationEnd),
          timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
          coverageState: energy.coverage === 'complete' ? EnergyCoverageState.Complete : EnergyCoverageState.CurrentDayPartial })]
      })));
    } catch { /* retry on the next launch/resume without affecting the app */ }
    finally { this.syncing = false; }
  }

  /**
   * Advances a durable past-log run by one bounded batch. Each native read is
   * still a local-day aggregate, so the only data that leaves the phone is the
   * approved normalized daily energy fact. The backend cursor makes app
   * termination, retry, and timezone replacement safe.
   */
  async syncHistoryIfConfigured(platform: 'AppleHealth' | 'HealthConnect'): Promise<HealthHistoryProgressResponse | null> {
    if (this.syncingHistory) return null;
    const installationId = await this.getInstallationId(platform);
    if (!installationId || !(await this.isSetupComplete(platform))) return null;
    this.syncingHistory = true;
    try {
      const progress = await firstValueFrom(this.api.historyRun(new HealthHistoryRunRequest({ installationId, platform })));
      if (progress.scope !== 'ThirtyDays' || progress.state !== 'Running') return progress;

      const today = this.localDateParts(new Date());
      // A cursor is supplied by the server for every new or reset past-log run.
      // Keep a bounded client fallback so a missing cursor can never recreate
      // the retired unbounded historical range.
      const fallbackStart = new Date(today);
      fallbackStart.setMonth(fallbackStart.getMonth() - 1);
      const start = this.parseLocalDate(progress.inboundCursor ?? this.formatLocalDate(fallbackStart));
      const days: DailyEnergyUpload[] = [];
      let next = start;
      for (let i = 0; i < 31 && next <= today; i++, next = this.addDays(next, 1)) {
        const localDate = this.formatLocalDate(next);
        const energy = await this.health.getDailyEnergySummary(localDate);
        if (!energy || energy.status !== 'data' || energy.activeCalories == null || !energy.observationStart || !energy.observationEnd) continue;
        days.push(new DailyEnergyUpload({
          localDate,
          activeCaloriesKcal: energy.activeCalories,
          totalCaloriesKcal: energy.totalCalories,
          totalMethod: energy.totalCaloriesMethod === 'provider_aggregate' ? EnergyTotalMethod.HealthConnectAggregate : EnergyTotalMethod.AppleActivePlusBasal,
          observationStartUtc: new Date(energy.observationStart),
          observationEndUtc: new Date(energy.observationEnd),
          timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
          coverageState: energy.coverage === 'complete' ? EnergyCoverageState.Complete : EnergyCoverageState.CurrentDayPartial
        }));
      }
      const nextCursor = next > today ? undefined : this.formatLocalDate(next);
      return await firstValueFrom(this.api.uploadDailyEnergyBatch(new UploadDailyEnergyBatchRequest({
        installationId, platform, days, nextCursor
      })));
    } catch {
      // A later foreground opportunity safely retries the same cursor.
      return null;
    } finally {
      this.syncingHistory = false;
    }
  }

  async getHistoryProgress(platform: 'AppleHealth' | 'HealthConnect'): Promise<HealthHistoryProgressResponse | null> {
    const [installationId, setupComplete] = await Promise.all([
      this.getInstallationId(platform),
      this.isSetupComplete(platform)
    ]);
    // An installation id outlives a disconnect so scoped meal cleanup can
    // remain possible. It must not, on its own, revive or display that
    // connection's old history run.
    if (!installationId || !setupComplete) return null;
    try { return await firstValueFrom(this.api.historyRun(new HealthHistoryRunRequest({ installationId, platform }))); }
    catch { return null; }
  }

  async cancelHistory(platform: 'AppleHealth' | 'HealthConnect'): Promise<HealthHistoryProgressResponse | null> {
    const installationId = await this.getInstallationId(platform);
    if (!installationId) return null;
    try { return await firstValueFrom(this.api.cancelHistoryRun(new HealthHistoryRunRequest({ installationId, platform }))); }
    catch { return null; }
  }

  private async isSetupComplete(platform: 'AppleHealth' | 'HealthConnect'): Promise<boolean> {
    return (await Preferences.get({ key: platform === 'AppleHealth' ? 'health-apple-outbound-setup-complete' : 'health-connect-inbound-setup-complete' })).value === 'true';
  }

  private async getInstallationId(platform: 'AppleHealth' | 'HealthConnect'): Promise<string | null> {
    return (await Preferences.get({ key: platform === 'AppleHealth' ? 'health-apple-installation-id' : 'health-connect-installation-id' })).value;
  }

  private localDateParts(date: Date): Date { return new Date(date.getFullYear(), date.getMonth(), date.getDate()); }
  private parseLocalDate(value: string): Date { const [year, month, day] = value.split('-').map(Number); return new Date(year, month - 1, day); }
  private formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  private addDays(date: Date, days: number): Date { const result = new Date(date); result.setDate(result.getDate() + days); return result; }
}
